// src/modules/chats/Chats.tsx
import React, { useState, useCallback, useMemo } from 'react';
import {
  Layout,
  List,
  Card,
  Button,
  Input,
  Space,
  Tag,
  Typography,
  Badge,
  Avatar,
  Row,
  Col,
  Empty,
  Spin,
  Tabs,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  RobotOutlined,
  WarningOutlined,
  SendOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useChats } from '@/hooks/useChats';
import { ChatSession } from '@/services/api/chats';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Sider, Content } = Layout;
const { TextArea } = Input;

// Цвета для статусов
const getModeColor = (mode: 'auto' | 'manual') => {
  return mode === 'manual' ? '#1890ff' : '#52c41a';
};

const getModeIcon = (mode: 'auto' | 'manual') => {
  return mode === 'manual' ? <UserOutlined /> : <RobotOutlined />;
};

interface FilterParams {
  requires_attention?: boolean;
  search: string;
  limit: number;
  offset: number;
}

export const Chats: React.FC = () => {
  const {
    chats,
    currentChat,
    messages,
    loading,
    messagesLoading,
    actionLoading,
    loadChats,
    loadChatMessages,
    sendMessage: sendMessageApi,
    takeControl,
    releaseControl,
    openChat,
    closeChat,
    refresh,
  } = useChats();

  // Активная вкладка: 'critical' или 'all'
  const [activeTab, setActiveTab] = useState<'critical' | 'all'>('critical');

  // Состояние фильтров
  const [filters, setFilters] = useState<FilterParams>({
    requires_attention: true,
    search: '',
    limit: 20,
    offset: 0,
  });

  // Сообщение для отправки
  const [messageText, setMessageText] = useState('');

  // Переключение вкладки
  const handleTabChange = useCallback((key: string) => {
    const tab = key as 'critical' | 'all';
    setActiveTab(tab);
    
    const newFilters: FilterParams = {
      requires_attention: tab === 'critical' ? true : undefined,
      search: '',
      limit: 20,
      offset: 0,
    };
    
    setFilters(newFilters);
    
    const params: any = { ...newFilters };
    if (params.requires_attention === undefined) delete params.requires_attention;
    if (!params.search) delete params.search;
    
    loadChats(params);
  }, [loadChats]);

  // Применение поиска
  const applySearch = useCallback(() => {
    const params: any = {
      ...filters,
      offset: 0,
    };
    
    if (!params.search) delete params.search;
    if (params.requires_attention === undefined) delete params.requires_attention;
    
    loadChats(params);
  }, [filters, loadChats]);

  // Открытие чата
  const handleOpenChat = useCallback(async (chat: ChatSession) => {
    await openChat(chat);
  }, [openChat]);

  // Отправка сообщения
  const handleSendMessage = useCallback(async () => {
    if (!currentChat || !messageText.trim()) return;
  
    try {
      await sendMessageApi(currentChat.id, messageText);
      setMessageText('');
      
      // Перезагружаем сообщения
      await loadChatMessages(currentChat.id);
      
      // Прокручиваем вниз после отправки
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  }, [currentChat, messageText, sendMessageApi, loadChatMessages]);

  // Взять в работу
  const handleTakeControl = useCallback(async () => {
    if (!currentChat) return;
    await takeControl(currentChat.id);
  }, [currentChat, takeControl]);

  // Вернуть боту
  const handleReleaseControl = useCallback(async () => {
    if (!currentChat) return;
    await releaseControl(currentChat.id);
  }, [currentChat, releaseControl]);

  // Обработчик изменения текста
  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
  }, []);

  // Обработчик Enter
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Подсчёт количества чатов для бейджей
  const criticalCount = useMemo(() => {
    return chats.filter(c => c.requires_attention).length;
  }, [chats]);

  // Список чатов (боковая панель)
  const ChatList = useMemo(() => (
    <div className="h-full flex flex-col">
      {/* Вкладки */}
      <Card size="small" className="mb-2" bodyStyle={{ padding: '8px 12px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'critical',
              label: (
                <span>
                  <WarningOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                  Требуют внимания
                  {criticalCount > 0 && (
                    <Badge 
                      count={criticalCount} 
                      style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }}
                    />
                  )}
                </span>
              ),
            },
            {
              key: 'all',
              label: (
                <span>
                  💬 Все чаты
                  <Badge 
                    count={chats.length} 
                    style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
                  />
                </span>
              ),
            },
          ]}
        />
      </Card>

      {/* Поиск и обновление */}
      <Card size="small" className="mb-2">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Input
            placeholder="Поиск по телефону..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onPressEnter={applySearch}
            size="small"
            allowClear
          />
          
          <Space>
            <Button 
              size="small" 
              type="primary" 
              onClick={applySearch} 
              loading={loading}
              icon={<SearchOutlined />}
            >
              Найти
            </Button>
            <Button 
              size="small" 
              icon={<ReloadOutlined />} 
              onClick={refresh} 
              loading={loading}
            >
              Обновить
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Список чатов */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Spin />
          </div>
        ) : chats.length === 0 ? (
          <Empty
            description="Нет чатов"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={chats}
            renderItem={(chat) => (
              <List.Item
                key={chat.id}
                onClick={() => handleOpenChat(chat)}
                className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                  currentChat?.id === chat.id ? 'bg-blue-50' : ''
                }`}
                style={{ padding: '12px 16px' }}
              >
                <List.Item.Meta
                  avatar={
                    <Badge dot={chat.requires_attention} status="error">
                      <Avatar icon={<UserOutlined />} />
                    </Badge>
                  }
                  title={
                    <div className="flex items-center justify-between">
                      <Text strong>{chat.customer_name || chat.customer_phone}</Text>
                      <Space size={4}>
                        {chat.requires_attention && (
                          <WarningOutlined style={{ color: '#ff4d4f' }} />
                        )}
                        <Tag 
                          color={getModeColor(chat.mode)} 
                          icon={getModeIcon(chat.mode)}
                          style={{ margin: 0 }}
                        >
                          {chat.mode === 'manual' ? 'Ручной' : 'Авто'}
                        </Tag>
                      </Space>
                    </div>
                  }
                  description={
                    <div>
                      <Paragraph 
                        ellipsis={{ rows: 1 }}
                        className="mb-1 text-xs"
                      >
                        {chat.last_message?.content || 'Нет сообщений'}
                      </Paragraph>
                      <Text type="secondary" className="text-xs">
                        {chat.last_message?.timestamp 
                          ? dayjs(chat.last_message.timestamp).format('HH:mm DD.MM')
                          : ''}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  ), [chats, currentChat, filters, loading, activeTab, criticalCount, applySearch, refresh, handleOpenChat, handleTabChange]);

  // Ref для автопрокрутки
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Автопрокрутка вниз при изменении сообщений
  React.useEffect(() => {
    if (messagesEndRef.current && !messagesLoading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, messagesLoading]);

  // Окно чата
  const ChatWindow = useMemo(() => {
    if (!currentChat) {
      return (
        <Empty
          description="Выберите чат из списка"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <div className="h-full flex flex-col">
        {/* Заголовок чата */}
        <Card size="small" className="mb-2">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <Title level={5} className="mb-0">
                  {currentChat.customer_name || currentChat.customer_phone}
                </Title>
                <Text type="secondary" className="text-xs">
                  {currentChat.customer_phone}
                </Text>
              </div>
            </Col>
            <Col>
              <Space>
                {currentChat.requires_attention && (
                  <Tag color="red" icon={<WarningOutlined />}>
                    Требует внимания
                  </Tag>
                )}
                
                <Tag 
                  color={getModeColor(currentChat.mode)} 
                  icon={getModeIcon(currentChat.mode)}
                >
                  {currentChat.mode === 'manual' ? 'Ручной режим' : 'Авто режим'}
                </Tag>
                
                {currentChat.mode === 'auto' ? (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleTakeControl}
                    loading={actionLoading}
                  >
                    Взять в работу
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={handleReleaseControl}
                    loading={actionLoading}
                    danger
                  >
                    Вернуть боту
                  </Button>
                )}

                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={closeChat}
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* История сообщений */}
        <Card className="flex-1 overflow-auto mb-2" bodyStyle={{ height: '100%', overflowY: 'auto' }}>
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <Spin />
          </div>
        ) : messages.length === 0 ? (
          <Empty description="Нет сообщений" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="space-y-2">
            {/* Статистика */}
            <div className="text-center text-xs bg-blue-50 p-2 rounded mb-2">
              📊 Всего: {messages.length} | 
              📥 Входящих: {messages.filter(m => m.direction === 'inbound').length} | 
              📤 Исходящих: {messages.filter(m => m.direction === 'outbound').length}
            </div>

            {messages.map((msg, index) => (
              <div
                key={msg.message_id || `msg-${index}`}
                className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg p-3 ${
                    msg.direction === 'outbound'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-black'
                  }`}
                >
                  <div className="text-xs opacity-50 mb-1">
                    {msg.direction === 'outbound' ? '📤 Бот' : '📥 Клиент'}
                  </div>
                  
                  <div className="whitespace-pre-wrap break-words">
                    {msg.content || '(пустое сообщение)'}
                  </div>
                  
                  <div className={`text-xs mt-1 ${
                    msg.direction === 'outbound' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {msg.timestamp 
                      ? dayjs(msg.timestamp).format('HH:mm:ss DD.MM.YYYY')
                      : '⚠️ Нет времени'
                    }
                  </div>
                </div>
              </div>
            ))}
            
            {/* Якорь для автопрокрутки */}
            <div ref={messagesEndRef} />
          </div>
        )}
        </Card>

        {/* Поле ввода (только в ручном режиме) */}
        {currentChat.mode === 'manual' && (
          <Card size="small">
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                placeholder="Введите сообщение..."
                value={messageText}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={actionLoading}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={actionLoading}
                disabled={!messageText.trim()}
              >
                Отправить
              </Button>
            </Space.Compact>
          </Card>
        )}
      </div>
    );
  }, [
    currentChat, 
    messages, 
    messagesLoading, 
    messageText, 
    actionLoading,
    handleTakeControl,
    handleReleaseControl,
    closeChat,
    handleMessageChange,
    handleKeyPress,
    handleSendMessage
  ]);

  return (
    <div className="p-6" style={{ height: 'calc(100vh - 112px)' }}>
      <div className="mb-4">
        <Title level={2} className="mb-1">Управление чатами</Title>
        <Text type="secondary">Общение с клиентами в WhatsApp</Text>
      </div>

      <Layout style={{ height: 'calc(100% - 60px)', background: '#fff' }}>
        {/* Боковая панель со списком чатов */}
        <Sider width={350} style={{ background: '#fafafa', borderRight: '1px solid #f0f0f0' }}>
          {ChatList}
        </Sider>

        {/* Основная область с чатом */}
        <Content style={{ padding: '16px' }}>
          {ChatWindow}
        </Content>
      </Layout>
    </div>
  );
};